import IThemeInterface, { breakPointSizes, edgeSize, zIndices } from './common';

const theme: IThemeInterface = {
  name: '',

  global: {
    breakPointSizes,

    colors: {
      black: '',
      error: '',
      focus: '',
      info: '',
      link: '',
      primary: '',
      secondary: '',
      success: '',
      warning: '',
      white: '#fff',
    },

    edgeSize,

    elevation: {
      '1x': {},
    },

    font: {
      family: {},
      size: {
        heroLarge: '3.2rem',
        heroLargeMobile: '',
        heroMedium: '2.4rem',
        heroMediumMobile: '',
        heroSmall: '2rem',
        heroSmallMobile: '',
        body: '1.6rem',
        bodyLarge: '1.8rem',
        bodyMedium: '1.4rem',
        bodySmall: '1.2rem',
      },
    },

    outline: {
      color: 'focus',
      size: 'single',
      style: 'solid',
    },

    zIndex: zIndices
  },

  banner: {
    info: {
      backgroundColor: '',
      border: {
        color: '',
      },
      closeIcon: {
        color: '',
        size: '',
      },
      inlineLink: {
        color: '',
        weight: '',
        hoverColor: '',
      },
      padding: '',
      font: { color: '' }, // optional
    },
  },

  barProgress: {
    borderRadius: '',
    track: {
      height: '',
      color: {
        base: '',
        progress: ''
      }
    }
  },

  button: {
    base: {
      background: 'primary',
      border: { color: 'primary' },
      borderRadius: '',
      justifyContent: '',
      shadow: 'none',
      icons: { mobileHidden: false },
      font: {
        color: 'white',
        size: 'bodyLarge',
        letterSpacing: '',
        textTransform: '',
        weight: ''
      },
      outline: 'none',
      padding: {},
      width: { min: '' },
      loadingIconInsideButton: false,
      spinner: { size: '4x' },
      additionalStyles: ''
    },
    overrides: {
      primary: {
        state: {
          disabled: {},
          hover: {},
          focus: {}
        },
      },

      secondary: {
        base: {
          background: 'secondary',
          font: { color: 'white' },
          border: { color: 'secondary' }
        },
        state: {
          disabled: {},
          hover: {},
          focus: {},
        },
      },

      text: {
        base: {
          background: 'none',
          font: { color: 'primary', size: 'body' },
          border: { color: 'transparent' },
        },
        state: {
          disabled: {},
          focus: {},
        },
      },

      link: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            textDecoration: 'underline',
            size: 'body',
            lineHeight: '',
          },
          border: { size: 'none' },
          padding: '',
        },
        state: {
          hover: {},
          focus: {},
          disabled: {},
        },
      },
    },
  },

  circleProgress: {
    strokeWidth: 3,
    backgroundPathColor: '',
    percentagePathColor: '',
    label: {
      percentage: {
        sign: {},
        number: {},
      },
      content: {},
    }
  },

  containerSize: {
    full: '100%',
    narrow: '',
    wide: '',
    max: '',
  },

  datePicker: {
    input: {
      iconColor: {
        base: 'black'
      }
    },
    navButton: {
      color: '',
      // tslint:disable:max-line-length
      nextIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='12'%3E%3Cpath fill='${encodeURIComponent(color)}' d='M9.53 5.1523l-8-4.9997A.998.998 0 0 0 .515.1256C.197.3026 0 .6366 0 1.0006V11c0 .364.197.698.515.875.152.083.318.125.485.125.184 0 .368-.051.53-.152l8-4.9998c.292-.183.47-.503.47-.848 0-.3449-.178-.6649-.47-.8479z'/%3E%3C/svg%3E%0A"`,
      prevIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='12'%3E%3Cpath fill='${encodeURIComponent(color)}' %20transform%3D%27translate%2810%2C%200%29%20scale%28-1%2C%201%29%27 d='M9.53 5.1523l-8-4.9997A.998.998 0 0 0 .515.1256C.197.3026 0 .6366 0 1.0006V11c0 .364.197.698.515.875.152.083.318.125.485.125.184 0 .368-.051.53-.152l8-4.9998c.292-.183.47-.503.47-.848 0-.3449-.178-.6649-.47-.8479z'/%3E%3C/svg%3E%0A"`,
      // tslint:enable:max-line-length
      backgroundSize: '50%'
    },
    outline: '',
    weekdayLabel: {
      color: ''
    },
    dayCell: {
      borderRadius: {},
      selected: {
        color: '',
        backgroundColor: '',
      },
      hover: {
        color: '',
        backgroundColor: ''
      },
      disabled: {
        color: ''
      },
    },
    overlay: {
      backgroundColor: 'white'
    }
  },

  descriptionList: {
    group: {
      margin: { bottom: '8px'},
    },
    term: {
      color: '',
      size: 'body',
      lineHeight: '',
      family: 'opensans',
      margin: '',
      weight: '400',
    },
    detail: {
      color: '',
      size: 'body',
      lineHeight: '',
      family: 'opensans',
      weight: '400',
    },

    variant: {
      large: {
        term: {
          color: '',
          size: 'body',
          lineHeight: '',
          family: 'opensans',
          margin: '',
          weight: '400',
        },

        detail: {
          color: '',
          size: 'body',
          lineHeight: '',
          family: 'opensans',
          weight: '400',
        },
      },
      small: {
        term: {
          color: '',
          size: 'body',
          lineHeight: '',
          family: 'opensans',
          margin: '',
          weight: '400',
        },

        detail: {
          color: '',
          size: 'body',
          lineHeight: '',
          family: 'opensans',
          weight: '400',
        },
      },
    }
  },

  form: {
    autoComplete: {
      list: {
        shadow: '',
        backgroundColor: '',
        height: { max: '' },
      },

      listItem: {
        padding: { bottom: '', top: '', horizontal: '' },
        margin: { bottom: '', top: '' },
        font: {
          size: 'body',
          color: '',
          lineHeight: '',
        },

        active: {
          backgroundColor: '',
          font: { color: '' }},
      },

      listItemHeader: {
        font: {
          weight: '400',
          color: '',
          size: 'body',
          lineHeight: '',
        }
      },
    },

    checkbox: {
      backgroundColor: 'white',
      border: { color: '' },
      icon: {
        checkedIconType: 'done',
        size: '',
        color: 'white',
      },
      label: {
        font: { color: 'text', size: 'body', lineHeight: '' }
      },
      round: '',
      size: '1x',
      shadow: '', // optional
      margin: { right: '' },

      state: {
        checked: {},
        checkedHover: {}, // optional
        disabled: {},
        disabledChecked: {},
        error: {},
        focus: {
          focusPosition: 'indicatorOnly',
        },
        hover: {},
      },
    },

    layout: {
      wrapper: {
        row: { margin: { bottom: '' } },
        stackRow: {
          margin: {},
        },
      },
    },

    errorText: {
      checkboxAndRadioErrorIndention: false, // optional
      font: {
        color: 'error',
        size: 'bodySmall',
      },
      margin: { top: '1x' },
    },

    helperText: {
      font: {
        color: {
          default: '',
          disabled: '', // optional
          error: '', // optional
          hover: '', // optional
          readOnly: '',
        },
        lineHeight: '',
        size: 'bodySmall',
        weight: '400',
      },
      margin: { top: '1x' },
    },

    input: {
      backgroundColor: 'white',
      border: { color: '', size: 'single', side: 'all' },
      iconColor: 'black',
      font: { color: '', lineHeight: '' }, // optional
      opacity: 1,
      padding: { horizontal: '1x', top: '1x', bottom: '1x' }, // horizontal is required
      placeholder: '', // color
      round: '',
      additionalStyles: '', // optional

      state: {
        hover: {},

        focus: {},

        error: {},

        disabled: {},

        readOnly: {},
      },
    },

    label: {
      font: {
        color: {
          default: '',
          disabled: '',
          error: '', // optional
          hover: '', // optional
          readOnly: '',
        },
        size: 'bodyMedium',
        weight: '500',
      },
      margin: { bottom: '1x' },
    },

    legend: {
      font: {
        size: 'body',
        color: '',
        weight: '400',
      },
      margin: { bottom: '' },
    },

    radio: {
      button: {
        backgroundColor: '',
        checkedStyle: 'ring',
        size: '',
        border: { color: 'primary' },
        label: { font: { color: '' }},
        shadow: {}, // optional

        state: {
          checked: {},
          checkedHover: {}, // optional
          disabled: {},
          disabledChecked: {},
          error: {},
          focus: {
            focusPosition: 'indicatorOnly',
          },
          hover: {},
        },
      },

      card: {
        backgroundColor: 'white',
        border: { color: 'transparent' },
        round: 'quadruple',
        shadow: '',
        outline: {}, // optional

        state: {
          checked: {},
          focus: {},
          hover: {},
          error: { text: { margin: { top: '' }}},
        },
      },

      badge: {
        backgroundColor: '',
        border: { color: 'gray500' },
        padding: '',
        round: 'quadruple',
        width: { min: '4.8125em' },
        shadow: '',
        font: { color: 'text', weight: '400' },
        outline: {}, // optional

        state: {
          checked: {},
          checkedHover: {},
          disabled: {},
          disabledChecked: {},
          error: {},
          focus: {},
          hover: {},
        },
      },

      toggle: {
        focusPosition: 'labelAndIndicator',

        label: {
          color: '',
          size: 'body',
          weight: '400',
          lineHeight: '1.2',
        },

        button: {
          height: '2.5x',
          width: '5x',
          margin: { right: '1x' },
          padding: { vertical: '', horizontal: '' },
          border: '',
          backgroundColor: '',
          outline: '',
          additionalStyles: ''
        },

        slidingIndicator: {
          size: '1x',
          color: '',
        },

        state: {
          checked: {
            label: {
              color: '',
              outline: '',
              shadow: '',
            },
            button: {
              border: '',
              backgroundColor: '',
              outline: '',
              shadow: '',
            },
            slidingIndicator: {
              color: '',
            },
          },
          checkedHover: {
            label: {
              color: '',
              outline: '',
              shadow: '',
            },
            button: {
              border: '',
              backgroundColor: '',
              outline: '',
              shadow: '',
            },
            slidingIndicator: {
              color: '',
            },
          },
          disabled: {
            label: {
              color: '',
              outline: '',
              shadow: '',
            },
            button: {
              border: '',
              backgroundColor: '',
              outline: '',
              shadow: '',
            },
            slidingIndicator: {
              color: '',
            },
          },
          disabledChecked: {
            label: {
              color: '',
              outline: '',
              shadow: '',
            },
            button: {
              border: '',
              backgroundColor: '',
              outline: '',
              shadow: '',
            },
            slidingIndicator: {
              color: '',
            },
          },
          focus: {
            label: {
              color: '',
              outline: '',
              shadow: '',
            },
            button: {
              border: '',
              backgroundColor: '',
              outline: '',
              shadow: '',
            },
            slidingIndicator: {
              color: '',
            },
          },
          hover: {
            label: {
              color: '',
              outline: '',
              shadow: '',
            },
            button: {
              border: '',
              backgroundColor: '',
              outline: '',
              shadow: '',
            },
            slidingIndicator: {
              color: '',
            },
          },
        },
      },
    },

    select: {
      icon: {
        type: 'block-arrow-down',
        size: '3x',
        color: '', // optional defaults to input color
        errorColor: '', // optional defaults to input color
      },
    },

    slider: {
      outline: { color: 'focus', offset: '14px' },
      thumb: {
        color: { default: '', disabled: '' },
        height: 36,
        width: 36,
      },
      track: {
        color: {
          base: { default: '', disabled: '' },
          progress: { default: '', disabled: '' }
        },
        thickness: 8,
        borderRadius: '4px',
      },
      unitLabels: {
        color: { default: '', disabled: '' }
      }
    },

    textarea: {
      backgroundColor: 'white',
      border: { color: '', size: 'single', side: 'all' },
      iconColor: 'black',
      font: { color: '', lineHeight: '' },
      opacity: 1,
      padding: { horizontal: '1x', top: '1x', bottom: '1x' }, // horizontal is required
      placeholder: '', // color
      round: '',
      additionalStyles: '', // optional

      state: {
        hover: {},

        focus: {},

        error: {},

        disabled: {},

        readOnly: {},
      },
    }
  },

  heading: {
    default: {
      color: 'black',
      family: 'effra',
      lineHeight: '',
    },

    level: {
      h1: {
        size: { base: 'heroMedium', medium: 'heroLarge' },
      },

      h2: {
        size: { base: 'heroSmall', medium: 'heroMedium' },
      },

      h3: {
        size: { base: 'bodyLarge', medium: 'heroSmall' },
      },

      h4: {/* level specific styles */ },
      h5: {/* level specific styles */ },
      h6: {/* level specific styles */ },
    },
  },

  layout: {
    header: {
      backgroundColor: 'white',
      border: 'none',
      padding: { left: '', right: '' },
      position: 'sticky',
      shadow: '',
      height: '',
      margin: { top: '' },
      width: '',

      link: {
        border: '',
        backgroundColor: '',
        padding: '',
        margin: { right: '' },
        font: {
          color: '',
          size: 'body',
          weight: '400',
          lineHeight: '',
          align: 'left'
        },
        state: {
          hover: {},
          focus: {},
          current: {},
        },
      },

      dropdown: {
        alignItems: '',
        backgroundColor: '',
        shadow: '',
        border: '',
        borderRadius: '',
        width: '',
        icon: {
          desktop: {
            size: '',
            color: '',
            type: 'arrow-bottom',
          },
        },
        padding: '',

        button: {
          border: '',
          backgroundColor: '',
          padding: '',
          margin: { right: '' },
          font: {
            color: '',
            size: 'body',
            lineHeight: '',
            weight: '400',
            align: 'left'
          },

          state: {
            hover: {},
            focus: {},
          },
        },

        link: {
          border: '',
          backgroundColor: '',
          padding: '',
          margin: { right: '' },
          font: {
            color: '',
            size: 'body',
            weight: '400',
            lineHeight: '',
            align: 'left'
          },
          state: {
            hover: {},
            focus: {},
          },
        },
      },
    },
    footer: {
      backgroundColor: '',
      border: 'none',
      padding: {},
      margin: { top: '' },
      height: '',
      width: '',
      font: {
        color: '',
        weight: '400',
        size: 'body',
        family: 'opensans',
      },
      link: {
        padding: '',
        margin: { right: '' },
        backgroundColor: '',
        border: '',
        font: {
          color: '',
          lineHeight: '',
          align: 'left',
          weight: '400',
          size: 'body',
          family: 'opensans',
        },
        state: {
          hover: {},
          focus: {},
        },
      },
    },
  },

  list: {
    margin: { left: '2x' },

    listItem: {
      common: {
        margin: { bottom: '1x' },
        font: {
          family: 'opensans',
          lineHeight: '',
          size: 'body',
          color: '',
          weight: '400'
        },
      },

      bullet: {
        listStyleSpacing: '',
        size: '',
        backgroundColor: '',
      },

      icon: {
        listStyleSpacing: '',
        type: 'done',
        size: '',
        color: '',
      },

      number: {
        listStyleSpacing: '',
        font: {
          weight: '400',
          color: '',
        },
      },
    },
  },

  link: {
    font: { color: '' },
    inline: {
      state: {
        hover: { color: '' },
      },
    },
  },

  modal: {
    overlay: {
      backgroundColor: '',
      padding: '',
    },
    card: {
      backgroundColor: '',
      border: '',
      padding: '',
      round: '',
      shadow: '',
    },
    closeIcon: {
      type: 'alert-close',
      size: '',
      color: '',
      padding: '',
    },
  },

  notification: {
    variant: {
      success: {
        border: {
          side: 'left',
          size: '6px',
          color: '',
          style: 'solid'
        },
        shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
        padding: ''
      },
      error: {
        border: {
          side: 'left',
          size: '6px',
          color: '',
          style: 'solid'
        },
        shadow: { shadow: '-2px 8px 8px 4px', color: 'rgba(0, 0, 0, 0.1)' },
        padding: ''
      },
    }
  },

  progressTracker: {
    desktop: {
      backgroundColor: '',
      stepSpacing: '',
      stepHr: {
        backgroundColor: '',
        bottomPositioning: '',
        height: '',
      },
      stepNumber: {
        backgroundColor: '',
        border: '',
        borderRadius: '',
        margin: { bottom: '', },
        size: '',
        iconColor: '',
        iconSize: '',
        iconType: 'done',
        font: {
          size: 'body',
          weight: '',
          lineHeight: '',
          color: '',
        },
      },
      stepName: {
        color: '',
        font: {
          size: 'body',
          weight: '',
        },
      },
      state: {
        active: {
          stepHr: {},
          stepNumber: {
            font: {},
          },
          stepName: {
            font: {},
          },
        },
        complete: {
          stepHr: {},
          stepNumber: {
            font: {},
          },
          stepName: {
            font: {},
          },
        },
      },
    },
    mobile: {
      stepCounter: {},
      stepName: {},
      upNext: {},
      nextStep: {},
    },
  },

  /*
    when spinner.withOutline = true; animatedOutline = transparent
    which gives the illusion that circleOutline is animated
  */
  spinner: {
    size: '',
    iconStrokeWidth: 0, // optional
    withOutline: true,
    color: {
      circleOutline: '',
      animatedOutline: '',
    },
  },

  table: {
    backgroundColor: '',
    border: '',
    borderSpacing: '', // '34px 0' horizontal vertical
    padding: {},
    font: {
      textIndent: '',
    },

    tableRow: {
      height: '',
      borderGutter: '',
    },

    tableHeader: {
      backgroundColor: '',
      rowDivider: '',
      padding: {},
      font: {
        size: 'body', // required
        color: '',
        lineHeight: '',
        weight: '400', // required
        textTransform: '',
      },
    },

    subHeader: {
      backgroundColor: '',
      border: '',
      font: {
        size: 'body', // required
        color: '',
        weight: '400', // required
      },
    },

    tableBody: {
      backgroundColor: '',
      rowDivider: '',
      borderRadius: {},
      boxShadow: '',
      padding: {},
      font: {
        size: 'body', // required
        color: '',
        lineHeight: '',
        weight: '400', // required
      },
    },
  },

  tooltip: {
    activationType: 'automatic',
    pane: {
      background: '',
      padding: '',
      shadow: '',
      round: '',
      position: '' as IThemeInterface['tooltip']['pane']['position'],
      border: '', // optional
      margin: {}, // optional

      font: {
        size: 'body',
        color: 'white',
        lineHeight: '1.4',
      },
    },

    icon: {
      size: '1em',
      color: 'primary',
      hover: '',
      margin: { left: 'double' },
    },
  },

  typography: {
    text: {
      body: {
        default: {
          color: '',
          lineHeight: '',
          size: 'body',
          margin: { bottom: '2x' },
          forwardedAs: 'p',
          weight: '400'
        },
        large: { size: 'bodyLarge' },
        medium: { size: 'bodyMedium' },
        small: { size: 'bodySmall' },
      },
    },

    pill: {
      default: {
        font: {
          weight: '400',
          color: '',
          lineHeight: '',
          transform: 'none',
          size: 'bodySmall'
        },
        padding: '',
        round: '',
        backgroundColor: '',
      },
    },
  },
};

export default theme;
